import { IonGrid } from '@ionic/react'
import { useLiveQuery } from 'dexie-react-hooks'
import { FC, useState } from 'react'

import db from 'mobile_web/modules/db'
import strings from 'mobile_web/strings'
import { ScreenSection } from 'mytours-read-common'
import { Button, Card, CardContent, CardHeader, CardTitle, BadgeGroup, BadgeGroupSection } from '@mytours/components'
import type { BadgeGroupItem } from '@mytours/components'
import SectionHeader from './SectionHeader'
import '../styles/sectionBadgeGroup.less'
import Modal from '../../../common/components/modal'
import { getDirMode } from '../../../platform'
import {
	badgeArtwork,
	groupProgress,
	listedBadges,
	toBadgeItem,
	useEarnedBadgeIds,
	useOpenBadge,
} from '../../../badges/badgeItems'

interface SectionBadgeGroupProps {
	section: ScreenSection
}

// Keeps the library's grid inside the app's heading and block shell, as the other adopted sections do.
const SectionBadgeGroup: FC<SectionBadgeGroupProps> = ({ section }) => {
	const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null)

	const sectionItems = section.section_items ?? []
	const badgeIds = sectionItems.flatMap(item => (item.badge_id ? [item.badge_id] : []))
	const groupIds = sectionItems.flatMap(item => (item.badge_group_id ? [item.badge_group_id] : []))
	// The section items are a fresh array on every store update, so the queries key on their ids.
	const idsKey = `${badgeIds.join(',')}|${groupIds.join(',')}`

	const badges = useLiveQuery(() => db.badges.where('id').anyOf(badgeIds).toArray(), [idsKey]) ?? []
	const groups = useLiveQuery(() => db.badgeGroups.where('id').anyOf(groupIds).toArray(), [idsKey]) ?? []
	// Feeds the group tiles' progress and the group modal.
	const groupBadges = useLiveQuery(() => db.badges.where('group_id').anyOf(groupIds).toArray(), [idsKey]) ?? []
	const earnedIds = useEarnedBadgeIds() ?? new Set<number>()
	const openBadge = useOpenBadge()

	const badgeItem = (badge: (typeof badges)[number]): BadgeGroupItem => {
		const earned = earnedIds.has(badge.id)
		return toBadgeItem(badge, earned, () => openBadge(badge, earned))
	}

	// An item whose record has not reached Dexie yet is left out rather than drawn without a name.
	const items = sectionItems.flatMap((item): BadgeGroupItem[] => {
		if (item.badge_id) {
			const badge = badges.find(candidate => candidate.id === item.badge_id)
			return badge ? [badgeItem(badge)] : []
		}
		const group = groups.find(candidate => candidate.id === item.badge_group_id)
		if (!group) {
			return []
		}
		const members = groupBadges.filter(badge => badge.group_id === group.id)
		return [
			{
				id: `group-${group.id}`,
				title: group.title ?? '',
				// The title is the button's only text, so a group without one would be unnamed.
				'aria-label': group.title || strings.badges,
				progress: groupProgress(members, earnedIds),
				onClick: () => setSelectedGroupId(group.id),
				...badgeArtwork(group.badge_group_media_item?.file),
			},
		]
	})

	const selectedGroup = groups.find(group => group.id === selectedGroupId)
	const selectedGroupItems = selectedGroup
		? listedBadges(
				groupBadges.filter(badge => badge.group_id === selectedGroup.id),
				earnedIds,
			).map(badgeItem)
		: []

	const closeGroup = () => setSelectedGroupId(null)

	return (
		<IonGrid className="section-badge-group" dir={getDirMode()}>
			<SectionHeader title={section.title} />
			<BadgeGroupSection className="block-content" dir={getDirMode()} dataCy="story-screen-section" items={items} />

			<Modal isOpen={!!selectedGroupId} onDidDismiss={closeGroup} cssClass="badge-group-modal">
				<Card variant="ios">
					<CardHeader className="badge-group-modal-header">
						<CardTitle as="h2">{selectedGroup?.title}</CardTitle>
						<Button variant="clear" className="close" onClick={closeGroup} aria-label={strings.close}>
							<i className="fal fa-xmark" />
						</Button>
					</CardHeader>
					<CardContent className="badge-group-modal-content">
						<BadgeGroup dir={getDirMode()} items={selectedGroupItems} />
					</CardContent>
				</Card>
			</Modal>
		</IonGrid>
	)
}

export default SectionBadgeGroup
